import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowRight, Filter, Zap, Thermometer, Building2, CheckCircle2, ShoppingCart, Eye, Search, ArrowUpDown, TrendingDown, Scale } from 'lucide-react';
import { useCart } from '@/lib/cartContext';
import { useCompare } from '@/lib/compareContext';
import { trackEvent, EVENTS } from '@/lib/analytics';
import { trackViewProductList, trackAddToQuoteCart } from '@/lib/ga4';
import { toast } from 'sonner';
import { SEO } from '@/components/SEO';
import { estimateMonthlySavings, formatINR, formatINRCompact, loadAssumptions } from '@/lib/savings';
import { AssumptionsFooter } from '@/components/AssumptionsDrawer';
import { MiniCartDrawer } from '@/components/MiniCartDrawer';
import { CompareBar } from '@/components/CompareBar';
import { fetchCatalogProducts } from '@/lib/catalogApi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const KW_RANGES = [
  { value: 'all', label: 'All Power' },
  { value: '0-200', label: '< 200 kW' },
  { value: '200-500', label: '200-500 kW' },
  { value: '500+', label: '500+ kW' },
];

const KWH_RANGES = [
  { value: 'all', label: 'All Energy' },
  { value: '0-300', label: '< 300 kWh' },
  { value: '300-700', label: '300-700 kWh' },
  { value: '700+', label: '700+ kWh' },
];

const TYPES = [
  { value: 'all', label: 'All Types' },
  { value: 'SYSTEM', label: 'Systems' },
  { value: 'BUNDLE', label: 'Bundles' },
];

const SORT_OPTIONS = [
  { value: 'popular', label: 'Most Popular' },
  { value: 'price-asc', label: 'Price: Low to High' },
  { value: 'price-desc', label: 'Price: High to Low' },
  { value: 'kw-desc', label: 'Power: Highest' },
  { value: 'kwh-desc', label: 'Energy: Highest' },
];

export default function ProductsPage() {
  const [searchParams] = useSearchParams();
  const powerParam = searchParams.get('power');
  const initialKw = powerParam === '<200' ? '0-200' : powerParam === '200-500' ? '200-500' : powerParam === '500+' ? '500+' : 'all';
  const [products, setProducts] = useState([]);
  const [catalogMeta, setCatalogMeta] = useState(null);
  const [filters, setFilters] = useState({ kw: initialKw, kwh: 'all', type: 'all' });
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('popular');
  const [miniCartOpen, setMiniCartOpen] = useState(false);
  const [lastAdded, setLastAdded] = useState(null);
  const { addItem } = useCart();
  const { addToCompare, removeFromCompare, isInCompare, canAdd } = useCompare();
  const assumptions = loadAssumptions();

  useEffect(() => {
    fetchCatalogProducts().then(({ products: data, source, fetchedAt, fallback }) => {
      const normalized = data.map(p => ({ ...p, sku: p.slug || p.sku }));
      setProducts(normalized);
      setCatalogMeta({ source, fetchedAt, count: normalized.length, fallback });
      trackViewProductList(window.location.pathname, normalized.length);
    });
  }, []);

  const filtered = products
    .filter(p => {
      if (filters.kw !== 'all') {
        const [min, max] = filters.kw === '500+' ? [500, Infinity] : filters.kw.split('-').map(Number);
        if (p.pcs_kw < min || p.pcs_kw > max) return false;
      }
      if (filters.kwh !== 'all') {
        const [min, max] = filters.kwh === '700+' ? [700, Infinity] : filters.kwh.split('-').map(Number);
        if (p.battery_kwh < min || p.battery_kwh > max) return false;
      }
      if (filters.type !== 'all' && p.sku_type !== filters.type) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        return (
          p.product_name?.toLowerCase().includes(q) ||
          p.slug?.toLowerCase().includes(q) ||
          p.vendor?.toLowerCase().includes(q) ||
          p.form_factor?.toLowerCase().includes(q) ||
          `${p.pcs_kw} kw`.includes(q) ||
          `${p.battery_kwh} kwh`.includes(q)
        );
      }
      return true;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'price-asc': return (a.buy_from_price_ex_gst || Infinity) - (b.buy_from_price_ex_gst || Infinity);
        case 'price-desc': return (b.buy_from_price_ex_gst || 0) - (a.buy_from_price_ex_gst || 0);
        case 'kw-desc': return b.pcs_kw - a.pcs_kw;
        case 'kwh-desc': return b.battery_kwh - a.battery_kwh;
        default: return 0;
      }
    });

  const handleAddToCart = (product, mode) => {
    addItem(product, mode);
    setLastAdded({ ...product, mode });
    setMiniCartOpen(true);
    trackEvent(EVENTS.ADD_TO_QUOTE_CART, { sku: product.sku, mode, source: 'products_page' });
    trackAddToQuoteCart(product, mode);
  };

  const formatPrice = (n) => n ? new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n) : null;

  return (
    <div data-testid="products-page">
      <SEO
        title="Products"
        path="/products"
        description="Browse Voltide's modular BESS product catalogue. From 125 kW to 750+ kW, available in compact enclosures or containerised formats."
      />
      {/* Compact Hero */}
      <section className="pt-10 pb-6">
        <div className="voltide-container">
          <div className="max-w-3xl animate-fade-up">
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#1A1A1A] leading-[1.1] mb-3">
              BESS Product Catalogue
            </h1>
            <p className="text-base text-[#57534E] leading-relaxed max-w-2xl">
              Modular systems from 125 kW to 750+ kW. Rent or buy — compare configurations and add to your quote cart.
            </p>
          </div>
        </div>
      </section>

      {/* Sticky Filters + Search + Sort + Products */}
      <section className="pb-16 lg:pb-20" data-testid="products-grid">
        <div className="voltide-container">
          {/* Search + Sort Row */}
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#A8A29E]" />
              <input
                type="text"
                placeholder="Search by kW, kWh, vendor, form factor..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 rounded-full border border-stone-200 text-sm focus:outline-none focus:border-[#0061DD] focus:ring-1 focus:ring-[#0061DD]/20"
                data-testid="product-search"
              />
            </div>
            <div className="relative shrink-0">
              <ArrowUpDown className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#A8A29E] pointer-events-none" />
              <select
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
                className="pl-10 pr-8 py-2.5 rounded-full border border-stone-200 text-sm bg-white appearance-none cursor-pointer focus:outline-none focus:border-[#0061DD]"
                data-testid="product-sort"
              >
                {SORT_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Sticky Filters */}
          <div className="sticky top-16 z-20 bg-[#F9F9F7]/95 backdrop-blur-sm py-3 -mx-4 px-4 mb-5 border-b border-stone-100" data-testid="product-filters">
            <div className="flex flex-wrap gap-2 items-center">
              <Filter className="w-4 h-4 text-[#57534E] shrink-0" />
              {KW_RANGES.map(opt => (
                <button key={opt.value} onClick={() => setFilters(prev => ({ ...prev, kw: opt.value }))}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${filters.kw === opt.value ? 'bg-[#0061DD] text-white shadow-md' : 'bg-white text-[#57534E] border border-stone-200 hover:border-[#0061DD]'}`}
                  data-testid={`filter-kw-${opt.value}`}>{opt.label}</button>
              ))}
              <div className="w-px h-5 bg-stone-200 mx-0.5 hidden sm:block" />
              {KWH_RANGES.map(opt => (
                <button key={opt.value} onClick={() => setFilters(prev => ({ ...prev, kwh: opt.value }))}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${filters.kwh === opt.value ? 'bg-[#0061DD] text-white shadow-md' : 'bg-white text-[#57534E] border border-stone-200 hover:border-[#0061DD]'}`}
                  data-testid={`filter-kwh-${opt.value}`}>{opt.label}</button>
              ))}
              <div className="w-px h-5 bg-stone-200 mx-0.5 hidden sm:block" />
              {TYPES.map(opt => (
                <button key={opt.value} onClick={() => setFilters(prev => ({ ...prev, type: opt.value }))}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${filters.type === opt.value ? 'bg-[#0061DD] text-white shadow-md' : 'bg-white text-[#57534E] border border-stone-200 hover:border-[#0061DD]'}`}
                  data-testid={`filter-type-${opt.value}`}>{opt.label}</button>
              ))}
              <span className="text-xs text-[#A8A29E] ml-auto">{filtered.length} products</span>
            </div>
          </div>

          {/* Product Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((product) => {
              const savings = estimateMonthlySavings(product.battery_kwh, assumptions);
              const buyPrice = product.buy_from_price_ex_gst;
              const rentPerKwh = product.rent_from_inr_per_kwh_month;
              const rentMonthly = product.rent_from_inr_per_month;
              return (
                <Card key={product.sku} className="bento-card border-0 shadow-none overflow-hidden group" data-testid={`product-card-${product.sku}`}>
                  <Link to={`/products/${product.sku}`} onClick={() => trackEvent(EVENTS.VIEW_PRODUCT, { sku: product.sku })}>
                    <div className="aspect-[4/3] bg-[#F9F9F7] flex items-center justify-center p-6 relative">
                      <img src={product.image} alt={product.product_name} className="max-h-full object-contain group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute top-3 right-3 flex flex-col gap-1">
                        <Badge className="rounded-full bg-blue-50 text-[#0061DD] border-blue-100 text-xs">{product.pcs_kw} kW</Badge>
                      </div>
                      <Badge className="absolute top-3 left-3 rounded-full bg-stone-100 text-[#57534E] text-xs">
                        {product.sku_type === 'BUNDLE' ? 'Bundle' : 'System'}
                      </Badge>
                      {product.featured && <Badge className="absolute bottom-3 left-3 rounded-full bg-amber-50 text-amber-700 border-amber-200 text-xs">Featured</Badge>}
                    </div>
                  </Link>
                  <CardHeader className="pb-1 pt-4 px-4">
                    <Link to={`/products/${product.sku}`}>
                      <CardTitle className="text-base font-bold text-[#1A1A1A] hover:text-[#0061DD] transition-colors leading-snug">{product.product_name}</CardTitle>
                    </Link>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="mono text-sm font-medium text-[#0061DD]">{product.battery_kwh} kWh</span>
                      <span className="text-xs text-[#A8A29E]">{[product.cooling, product.form_factor].filter(Boolean).join(' · ') || product.sku_type}</span>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3 px-4 pb-4">
                    {/* Savings estimate */}
                    <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 w-fit">
                      <TrendingDown className="w-3 h-3 text-emerald-600" />
                      <span className="text-xs font-medium text-emerald-700">
                        Save {formatINRCompact(savings.low)}–{formatINRCompact(savings.high)}/mo vs DG
                      </span>
                    </div>

                    {/* Pricing */}
                    <div className="flex justify-between text-sm border-t border-stone-100 pt-2">
                      <div>
                        <p className="text-[10px] text-[#A8A29E] uppercase tracking-wider">Buy (from)</p>
                        {buyPrice ? (
                          <p className="mono font-semibold text-[#1A1A1A]">{formatPrice(buyPrice)}</p>
                        ) : (
                          <p className="text-xs font-medium text-[#0061DD]">Request quote</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-[10px] text-[#A8A29E] uppercase tracking-wider">Rent (from)</p>
                        {rentPerKwh ? (
                          <p className="mono font-semibold text-[#0061DD]">₹{Number(rentPerKwh).toFixed(2)}/kWh/mo</p>
                        ) : (
                          <p className="text-xs font-medium text-[#0061DD]">Request quote</p>
                        )}
                      </div>
                    </div>
                    {product.disclaimer_text && <p className="text-[10px] text-[#A8A29E]">{product.disclaimer_text}</p>}

                    <div className="flex gap-2">
                      <Button onClick={() => handleAddToCart(product, 'rent')} className="flex-1 rounded-full bg-[#0061DD] hover:bg-[#004BB3] text-white gap-1 text-xs py-2" data-testid={`add-cart-${product.sku}`}>
                        <ShoppingCart className="w-3.5 h-3.5" /> Add to Cart
                      </Button>
                      <button
                        onClick={() => isInCompare(product.sku) ? removeFromCompare(product.sku) : (canAdd ? addToCompare(product) : toast.info('Max 3 products to compare'))}
                        className={`px-2 rounded-full border transition-all ${isInCompare(product.sku) ? 'bg-[#0061DD] border-[#0061DD] text-white' : 'border-stone-200 text-[#57534E] hover:border-[#0061DD] hover:text-[#0061DD]'}`}
                        title={isInCompare(product.sku) ? 'Remove from compare' : 'Add to compare'}
                        data-testid={`compare-toggle-${product.sku}`}
                      >
                        <Scale className="w-3.5 h-3.5" />
                      </button>
                      <Link to={`/products/${product.sku}`}>
                        <Button variant="outline" className="rounded-full border-stone-200 text-[#57534E] hover:text-[#0061DD] gap-1 text-xs py-2" data-testid={`view-detail-${product.sku}`}>
                          <Eye className="w-3.5 h-3.5" /> Details
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {filtered.length === 0 && products.length > 0 && (
            <div className="text-center py-16">
              <p className="text-[#57534E]">No products match your filters. Try adjusting your selection.</p>
              <Button onClick={() => { setFilters({ kw: 'all', kwh: 'all', type: 'all' }); setSearchQuery(''); }} variant="outline" className="mt-4 rounded-full" data-testid="clear-filters-btn">
                Clear Filters
              </Button>
            </div>
          )}

          {products.length === 0 && (
            <div className="text-center py-16">
              <p className="text-[#57534E]">Loading products...</p>
            </div>
          )}

          {/* Assumptions */}
          <div className="mt-6">
            <AssumptionsFooter />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 lg:py-16 bg-white" data-testid="products-cta">
        <div className="voltide-container text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#1A1A1A] mb-3">Not sure which system fits?</h2>
          <p className="text-[#57534E] mb-6 max-w-xl mx-auto">Tell us your requirements and we'll recommend the optimal configuration.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/quote">
              <Button className="rounded-full bg-[#0061DD] hover:bg-[#004BB3] text-white px-8 py-5 text-lg font-semibold gap-2" data-testid="products-cta-quote-btn">
                Get a Recommendation <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
            <Link to="/calculator">
              <Button variant="outline" className="rounded-full border-[#0061DD] text-[#0061DD] px-8 py-5 text-lg font-medium">
                Calculate Savings
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <MiniCartDrawer open={miniCartOpen} onClose={() => setMiniCartOpen(false)} lastItem={lastAdded} />
      <CompareBar />
    </div>
  );
}
